import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, AlertTriangle, CheckCircle, Info, Lightbulb,
  Shield, ArrowRight, Zap, Eye, EyeOff, Activity, Search, Heart
} from 'lucide-react';
import { sexualHealthData } from './SexualHealthData';

const SexualHealthCarousels = () => {
  const [mythRevealed, setMythRevealed] = useState({});
  const [currentMythIndex, setCurrentMythIndex] = useState(0);
  const [currentSafetyIndex, setCurrentSafetyIndex] = useState(0);
  
  const [currentAnatomyIndex, setCurrentAnatomyIndex] = useState(0);
  const [currentHealthIndex, setCurrentHealthIndex] = useState(0);

  const [activeMythSection, setActiveMythSection] = useState('overview');
  const [activeFactsSection, setActiveFactsSection] = useState('overview');

  const renderMyths = () => {
    const toggleReveal = (mythId) => {
      setMythRevealed(prev => ({
        ...prev,
        [mythId]: !prev[mythId]
      }));
    };

    const nextMyth = () => {
      setCurrentMythIndex((prev) => 
        (prev + 1) % sexualHealthData.mythBusters.popularMyths.length
      );
      setMythRevealed({});
    };

    const prevMyth = () => {
      setCurrentMythIndex((prev) => 
        (prev - 1 + sexualHealthData.mythBusters.popularMyths.length) % sexualHealthData.mythBusters.popularMyths.length
      );
      setMythRevealed({});
    };

    const nextSafety = () => {
      setCurrentSafetyIndex((prev) => 
        (prev + 1) % sexualHealthData.mythBusters.safetyMyths.length
      );
    };

    const prevSafety = () => {
      setCurrentSafetyIndex((prev) => 
        (prev - 1 + sexualHealthData.mythBusters.safetyMyths.length) % sexualHealthData.mythBusters.safetyMyths.length
      );
    };

    const renderOverview = () => (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-full flex items-center justify-center">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Myth Busters</h2>
              <p className="text-xs sm:text-sm text-gray-600">Facts vs Fiction</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
          {[
            { 
              id: "popular",
              title: "Popular Myths", 
              icon: AlertTriangle, 
              color: "red", 
              description: "Separating fact from fiction",
              count: sexualHealthData.mythBusters.popularMyths.length,
            },
            { 
              id: "safety",
              title: "Safety Myths", 
              icon: Shield, 
              color: "orange", 
              description: "Dangerous misconceptions debunked",
              count: sexualHealthData.mythBusters.safetyMyths.length,
            }
          ].map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveMythSection(item.id)}
              className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 group cursor-pointer ${
                item.color === 'red' ? 'border-red-500 hover:border-red-600' :
                item.color === 'orange' ? 'border-orange-500 hover:border-orange-600' : 'border-gray-500'
              }`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <item.icon className={`w-8 h-8 sm:w-10 sm:h-10 mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform ${
                item.color === 'red' ? 'text-red-600' :
                item.color === 'orange' ? 'text-orange-600' : 'text-gray-600'
              }`} />
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
                item.color === 'red' ? 'bg-red-100 text-red-700' :
                item.color === 'orange' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {item.count} myths
              </div>
              <div className="mt-3 text-xs text-gray-400 flex items-center justify-center gap-1">
                <span>Click to explore</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-6 sm:space-y-8">
        {activeMythSection === 'overview' && renderOverview()}
        
        {activeMythSection === 'popular' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveMythSection('overview')}
                className="flex items-center gap-2 text-red-600 hover:text-red-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-4"
            >
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">🔍 Popular Myths</h3>
                <p className="text-gray-600 text-sm">Separating fact from fiction</p>
              </div>

              <div className="flex justify-between items-center mb-4 px-4">
                <button 
                  onClick={prevMyth}
                  className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
                >
                  <ArrowRight className="w-4 h-4 rotate-180 text-gray-600" />
                </button>
                
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">
                    {currentMythIndex + 1} of {sexualHealthData.mythBusters.popularMyths.length}
                  </div>
                  <div className="flex gap-1">
                    {sexualHealthData.mythBusters.popularMyths.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentMythIndex ? 'bg-red-500 w-6' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                
                <button 
                  onClick={nextMyth}
                  className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
                >
                  <ArrowRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentMythIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mx-4"
                >
                  {(() => {
                    const myth = sexualHealthData.mythBusters.popularMyths[currentMythIndex];
                    const isRevealed = mythRevealed[`reality-${currentMythIndex}`];
                    
                    return (
                      <div>
                        <div className="bg-red-50 p-4 border-b border-red-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-6 h-6 bg-red-100 rounded-lg flex items-center justify-center">
                              <AlertTriangle className="w-3 h-3 text-red-600" />
                            </div>
                            <span className="text-red-600 text-sm font-semibold">MYTH #{currentMythIndex + 1}</span>
                          </div>
                          <p className="text-gray-800 font-medium leading-relaxed text-sm">
                            "{myth.myth}"
                          </p>
                        </div>

                        <div className="p-4 text-center">
                          <button
                            onClick={() => toggleReveal(`reality-${currentMythIndex}`)}
                            className={`w-full py-2 px-4 rounded-lg font-medium transition-all text-sm ${
                              isRevealed 
                                ? 'bg-gray-100 text-gray-600' 
                                : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                            }`}
                          >
                            {isRevealed ? 'Hide Truth' : 'Reveal Truth'}
                          </button>
                        </div>

                        {isRevealed && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="border-t border-gray-100"
                          >
                            <div className="bg-green-50 p-4">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                </div>
                                <span className="text-green-600 text-sm font-semibold">THE TRUTH</span>
                              </div>
                              <p className="text-gray-800 font-medium mb-4 text-sm">{myth.reality}</p>
                              
                              <div className="space-y-2">
                                <div className="bg-blue-50 p-3 rounded-lg">
                                  <h5 className="text-blue-800 font-semibold text-xs mb-1">Evidence</h5>
                                  <p className="text-blue-700 text-xs">{myth.evidence}</p>
                                </div>
                                <div className="bg-orange-50 p-3 rounded-lg">
                                  <h5 className="text-orange-800 font-semibold text-xs mb-1">Why It Matters</h5>
                                  <p className="text-orange-700 text-xs">{myth.impact}</p>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  })()}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        )}

        {activeMythSection === 'safety' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveMythSection('overview')}
                className="flex items-center gap-2 text-orange-600 hover:text-orange-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-4"
            >
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">🚨 Safety Myths</h3>
                <p className="text-gray-600 text-sm">Dangerous misconceptions debunked</p>
              </div>

              <div className="flex justify-between items-center mb-4 px-4">
                <button 
                  onClick={prevSafety}
                  className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
                >
                  <ArrowRight className="w-4 h-4 rotate-180 text-gray-600" />
                </button>
                
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">
                    {currentSafetyIndex + 1} of {sexualHealthData.mythBusters.safetyMyths.length}
                  </div>
                  <div className="flex gap-1">
                    {sexualHealthData.mythBusters.safetyMyths.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentSafetyIndex ? 'bg-orange-500 w-6' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                
                <button 
                  onClick={nextSafety}
                  className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
                >
                  <ArrowRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSafetyIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mx-4"
                >
                  {(() => {
                    const myth = sexualHealthData.mythBusters.safetyMyths[currentSafetyIndex];
                    
                    return (
                      <div>
                        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="font-bold text-sm">DANGER MYTH #{currentSafetyIndex + 1}</span>
                          </div>
                        </div>

                        <div className="bg-yellow-50 p-4 border-b border-yellow-100">
                          <p className="text-gray-800 font-medium leading-relaxed text-sm">
                            "{myth.myth}"
                          </p>
                        </div>

                        <div className="bg-red-50 p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-6 h-6 bg-red-100 rounded-lg flex items-center justify-center">
                              <AlertTriangle className="w-3 h-3 text-red-600" />
                            </div>
                            <span className="text-red-600 text-sm font-semibold">THE REALITY</span>
                          </div>
                          <p className="text-gray-800 font-medium mb-3 text-sm">{myth.reality}</p>
                          
                          <div className="bg-orange-50 p-3 rounded-lg">
                            <h5 className="text-orange-800 font-semibold text-xs mb-1">Risk Level</h5>
                            <p className="text-orange-700 text-sm font-bold">{myth.riskLevel}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        )}

      </div>
    );
  };

  const renderFacts = () => {
    const nextAnatomy = () => {
      setCurrentAnatomyIndex((prev) => 
        (prev + 1) % sexualHealthData.didYouKnow.anatomyFacts.length
      );
    };

    const prevAnatomy = () => {
      setCurrentAnatomyIndex((prev) => 
        (prev - 1 + sexualHealthData.didYouKnow.anatomyFacts.length) % sexualHealthData.didYouKnow.anatomyFacts.length
      );
    };

    const nextHealth = () => {
      setCurrentHealthIndex((prev) => 
        (prev + 1) % sexualHealthData.didYouKnow.healthFacts.length
      );
    };

    const prevHealth = () => {
      setCurrentHealthIndex((prev) => 
        (prev - 1 + sexualHealthData.didYouKnow.healthFacts.length) % sexualHealthData.didYouKnow.healthFacts.length
      );
    };

    const renderOverview = () => (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Did You Know?</h2>
              <p className="text-xs sm:text-sm text-gray-600">Mind-blowing facts about sexual health</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
          {[
            { 
              id: "anatomy",
              title: "Anatomy Facts", 
              icon: Eye, 
              color: "blue", 
              description: "Amazing discoveries about the body",
              count: sexualHealthData.didYouKnow.anatomyFacts.length,
            },
            { 
              id: "health",
              title: "Health Benefits", 
              icon: Activity, 
              color: "green", 
              description: "Scientific research on wellness",
              count: sexualHealthData.didYouKnow.healthFacts.length,
            }
          ].map((item, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveFactsSection(item.id)}
              className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 text-center hover:shadow-xl transition-all border-l-4 group cursor-pointer ${
                item.color === 'blue' ? 'border-blue-500 hover:border-blue-600' :
                item.color === 'green' ? 'border-green-500 hover:border-green-600' : 'border-gray-500'
              }`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <item.icon className={`w-8 h-8 sm:w-10 sm:h-10 mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform ${
                item.color === 'blue' ? 'text-blue-600' :
                item.color === 'green' ? 'text-green-600' : 'text-gray-600'
              }`} />
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
              <div className={`text-xs font-medium px-3 py-1 rounded-full inline-block ${
                item.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                item.color === 'green' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {item.count} facts
              </div>
              <div className="mt-3 text-xs text-gray-400 flex items-center justify-center gap-1">
                <span>Click to explore</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-6 sm:space-y-8">
        {activeFactsSection === 'overview' && renderOverview()}
        
        {activeFactsSection === 'anatomy' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveFactsSection('overview')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-4"
            >
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">🧠 Anatomy & Science Facts</h3>
                <p className="text-gray-600 text-sm">Amazing discoveries about the human body</p>
              </div>

              <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 shadow-lg border border-blue-200">
                <div className="flex justify-between items-center mb-4">
                  <button 
                    onClick={prevAnatomy}
                    className="bg-white shadow-md rounded-full p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                  </button>
                  
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">
                      {currentAnatomyIndex + 1} of {sexualHealthData.didYouKnow.anatomyFacts.length}
                    </div>
                    <div className="flex gap-1">
                      {sexualHealthData.didYouKnow.anatomyFacts.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentAnatomyIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentAnatomyIndex ? 'bg-blue-600 w-6' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={nextAnatomy}
                    className="bg-white shadow-md rounded-full p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentAnatomyIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl shadow-lg p-4 sm:p-6"
                  >
                    {(() => {
                      const fact = sexualHealthData.didYouKnow.anatomyFacts[currentAnatomyIndex];
                      
                      return (
                        <div>
                          <div className="text-center mb-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                              <Eye className="w-6 h-6 text-blue-600" />
                            </div>
                            <h4 className="text-lg font-bold text-blue-700 mb-3">
                              FACT #{currentAnatomyIndex + 1}
                            </h4>
                            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                              <p className="text-sm font-semibold text-blue-800">
                                {fact.fact}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4">
                              <div className="flex items-start gap-2">
                                <Info className="w-4 h-4 text-indigo-600 mt-1 flex-shrink-0" />
                                <div>
                                  <h5 className="text-sm font-bold text-indigo-800 mb-2">Context</h5>
                                  <p className="text-sm text-indigo-700">{fact.context}</p>
                                </div>
                              </div>
                            </div>

                            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                              <div className="flex items-start gap-2">
                                <Lightbulb className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                                <div>
                                  <h5 className="text-sm font-bold text-green-800 mb-2">Why It Matters</h5>
                                  <p className="text-sm text-green-700">{fact.significance}</p>
                                </div>
                              </div>
                            </div>

                            <div className="text-center">
                              <span className="inline-block text-xs px-3 py-2 bg-gray-100 text-gray-700 rounded-full font-medium">
                                Category: {fact.category}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}

        {activeFactsSection === 'health' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveFactsSection('overview')}
                className="flex items-center gap-2 text-green-600 hover:text-green-800 transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to Overview</span>
              </button>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-4"
            >
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">💪 Health & Wellness Benefits</h3>
                <p className="text-gray-600 text-sm">Scientific research on wellness benefits</p>
              </div>

              <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 shadow-lg border border-green-200">
                <div className="flex justify-between items-center mb-4">
                  <button 
                    onClick={prevHealth}
                    className="bg-white shadow-md rounded-full p-2 text-green-600 hover:bg-green-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                  </button>
                  
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">
                      {currentHealthIndex + 1} of {sexualHealthData.didYouKnow.healthFacts.length}
                    </div>
                    <div className="flex gap-1">
                      {sexualHealthData.didYouKnow.healthFacts.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentHealthIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentHealthIndex ? 'bg-green-600 w-6' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={nextHealth}
                    className="bg-white shadow-md rounded-full p-2 text-green-600 hover:bg-green-50 transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentHealthIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl shadow-lg p-4 sm:p-6"
                  >
                    {(() => {
                      const fact = sexualHealthData.didYouKnow.healthFacts[currentHealthIndex];
                      
                      return (
                        <div>
                          <div className="text-center mb-4">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                              <Activity className="w-6 h-6 text-green-600" />
                            </div>
                            <h4 className="text-lg font-bold text-green-700 mb-3">
                              HEALTH BENEFIT #{currentHealthIndex + 1}
                            </h4>
                            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                              <p className="text-sm font-semibold text-green-800">
                                {fact.fact}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                              <div className="flex items-start gap-2">
                                <Search className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                                <div>
                                  <h5 className="text-sm font-bold text-blue-800 mb-2">Research</h5>
                                  <p className="text-sm text-blue-700">{fact.research}</p>
                                </div>
                              </div>
                            </div>

                            <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-4">
                              <div className="flex items-start gap-2">
                                <Heart className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                                <div>
                                  <h5 className="text-sm font-bold text-emerald-800 mb-2">Benefit</h5>
                                  <p className="text-sm text-emerald-700">{fact.benefit}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}

      </div>
    );
  };

  return {
    myths: renderMyths(),
    facts: renderFacts()
  };
};

export default SexualHealthCarousels;